import { TStore } from '@components/types';
import { humanizeCurrency, sanityIoImageLoader, getSafeUrl } from '@core/utils';
import { ChevronRight, MapPin, Star } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';

// Hotels worth showing first: has a photo and a real starting price (min_price is 0 when a hotel has
// no published rate, and max_price must never be shown as a "from" price), then most-reviewed / best-rated.
export const pickFeaturedStays = (stays: TStore[], limit = 8) =>
    (stays || [])
        .filter((s) => s?.id > 0 && s.image && (s as any).min_price > 0)
        .sort((a, b) =>
            (b.stats?.rating_count || 0) - (a.stats?.rating_count || 0) ||
            (b.stats?.rating_overall || 0) - (a.stats?.rating_overall || 0))
        .slice(0, limit);

export const FeaturedStays: React.FC<{ stays: TStore[] }> = ({ stays }) => {
    if (!stays?.length) return null;
    return (
        <section aria-label="Featured hotels" className="px-2">
            <div className="flex items-end justify-between gap-3 mb-3">
                <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-brand-500 m-0 mb-1">Hotels &amp; stays</p>
                    <h2 className="text-lg md:text-xl font-bold font-manrope text-neutral-900 m-0">Featured stays</h2>
                </div>
                <Link href="/stays" prefetch={false} className="flex items-center gap-1 text-sm font-semibold text-business-500 no-underline hover:underline whitespace-nowrap">
                    View all <ChevronRight size={14} />
                </Link>
            </div>
            <ul className="flex gap-4 overflow-x-auto pb-2 list-none p-0 m-0 snap-x" style={{ scrollbarWidth: 'thin' }}>
                {stays.map((s, i) => {
                    const price = (s as any).min_price as number;
                    const rating = s.stats?.rating_overall;
                    const place = [s.address?.locality, s.address?.city?.name].filter(Boolean).join(', ');
                    return (
                        <li key={s.id} className="flex-shrink-0 w-64 snap-start">
                            <Link href={`/stays/${s.id}/${getSafeUrl(s.name)}`} prefetch={false}
                                className="group block rounded-2xl overflow-hidden bg-white border border-solid border-neutral-100 shadow-sm hover:shadow-lg transition-shadow no-underline text-neutral-900">
                                <div className="relative h-40 bg-neutral-100">
                                    <Image loader={sanityIoImageLoader} src={s.image} alt={s.name} fill sizes="256px"
                                        className="object-cover group-hover:scale-105 transition-transform duration-300"
                                        loading={i < 2 ? 'eager' : 'lazy'} />
                                    {rating > 0 && (
                                        <span className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-lg bg-white/95 px-2 py-0.5 text-xs font-bold text-neutral-800 shadow">
                                            <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {Number(rating).toFixed(1)}
                                        </span>
                                    )}
                                </div>
                                <div className="p-3 flex flex-col gap-1">
                                    <h3 className="text-sm font-bold m-0 line-clamp-1">{s.name}</h3>
                                    {place && (
                                        <p className="flex items-center gap-1 text-xs text-neutral-500 m-0 line-clamp-1">
                                            <MapPin className="w-3 h-3 flex-shrink-0" /> {place}
                                        </p>
                                    )}
                                    <p className="m-0 mt-1 text-sm">
                                        <span className="text-xs text-neutral-400">From </span>
                                        <span className="font-bold">{humanizeCurrency(price)}</span>
                                        <span className="text-xs text-neutral-400"> / night + taxes</span>
                                    </p>
                                </div>
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
};
